<script>
import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation";
import {useDefaultPaySectionStore} from "@/stores/DefaultPaySection.js";
import useSharedData from "@/use/SharedData";
import useTools from "@/use/Tools";
import useSharedMethod from "@/use/SharedMethod";
import axios from "axios";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const {mxRules} = useValidation();
        const readonly = computed(() => props.deleteAction || props.viewAction);
        const {loading, overlay, dialog, inputColor, backgroundColor} = useSharedData();
        const {$route, $refs, $i18n} = useTools();
        const {getBack} = useSharedMethod();
        const defaultPaySectionStore = useDefaultPaySectionStore();
        
        
        const defaultPaySection = ref({
            type: null,
            imposable: null,
            conditioned: false,
            regularization: false,
            cotisable: false,
            brute: false,
            conge: false,
            isAbsenceRetenue: false,
        });
        
        const types = [
            {id: null, name: $i18n.t('common.none')},
            {id: "1", name: $i18n.t('pay_section.type_1')},
            {id: "2", name: $i18n.t('pay_section.type_2')},
            {id: "3", name: $i18n.t('pay_section.type_3')},
            {id: "4", name: $i18n.t('pay_section.type_4')},
            {id: "5", name: $i18n.t('pay_section.type_5')},
            {id: "6", name: $i18n.t('pay_section.type_6')},
            {id: "7", name: $i18n.t('pay_section.type_7')},
            {id: "8", name: $i18n.t('pay_section.type_8')},
            {id: "9", name: $i18n.t('pay_section.type_9')},
        ];
        const impositions = [
            {id: null, name: $i18n.t('common.none')},
            {id: "0", name: $i18n.t('pay_section.imposable_0')},
            {id: "1", name: $i18n.t('pay_section.imposable_1')},
            {id: "2", name: $i18n.t('pay_section.imposable_2')},
        ];
        
        
        function findById(id) {
            loading.value = true;
            axios.get(`/defaultPaySections/${id}`).then((response) => {
                defaultPaySection.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/defaultPaySections', defaultPaySection.value).then((response) => {
                    defaultPaySectionStore.addDefaultPaySection(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/defaultPaySections', defaultPaySection.value).then((response) => {
                    defaultPaySectionStore.updateDefaultPaySection(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function remove() {
            loading.value = true;
            axios.delete(`/defaultPaySections/${defaultPaySection.value.id}`).then(() => {
                defaultPaySectionStore.deleteDefaultPaySection(defaultPaySection.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        })
        
        return {
            mxRules,
            readonly,
            defaultPaySectionStore,
            loading,
            overlay,
            dialog,
            inputColor,
            backgroundColor,
            defaultPaySection,
            impositions,
            types,
            save,
            update,
            remove,
            getBack,
        }
    },
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="850"
        scrollable
    >
        <v-card flat>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                @cancel="overlay = false"
                @remove="remove"
            ></w-overlay>
            <v-card-text>
                <v-container fluid>
                    <v-form ref="form" lazy-validation>
                        <w-fieldset title="Définition">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('module.pay_section')" required/>
                                    <text-field-pay-section
                                        v-model="defaultPaySection.paySectionCode"
                                        :rules="[mxRules.required]"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <v-row dense>
                            <v-col cols="4">
                                <v-switch
                                    v-model="defaultPaySection.conditioned"
                                    :label="$t('pay_section.depend_on')"
                                    inset
                                    hide-details
                                    :readonly="readonly"
                                />
                            </v-col>
                        </v-row>
                        <br/>
                        <w-fieldset :title="$t('pay_section.type')">
                            <v-row dense>
                                <v-col cols="12">
                                    <v-radio-group
                                        v-model="defaultPaySection.type"
                                        row
                                        :disabled="!defaultPaySection.conditioned"
                                        class="mt-n2"
                                        hide-details="auto"
                                    >
                                        <v-radio
                                            v-for="(type, index) in types"
                                            :key="index"
                                            :value="type.id"
                                            :label="type.name"
                                            :readonly="readonly"
                                        ></v-radio>
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="$t('common.options')">
                            <v-row dense>
                                <v-col cols="12">
                                    <v-radio-group
                                        v-model="defaultPaySection.imposable"
                                        row
                                        :disabled="!defaultPaySection.conditioned"
                                        class="mt-n2"
                                        hide-details="auto"
                                    >
                                        <v-radio
                                            v-for="(imposition, index) in impositions"
                                            :key="index"
                                            :value="imposition.id"
                                            :label="imposition.name"
                                            color="red"
                                            :readonly="readonly"
                                        ></v-radio>
                                    </v-radio-group>
                                </v-col>
                                <v-col cols="6">
                                    <v-checkbox
                                        v-model="defaultPaySection.cotisable"
                                        :label="`${$t('pay_section.in_cotisable')}`"
                                        true-value="1"
                                        false-value="0"
                                        hide-details
                                        color="orange"
                                        :disabled="!defaultPaySection.conditioned"
                                        :readonly="readonly"
                                    ></v-checkbox>
                                </v-col>
                                <v-col cols="6">
                                    <v-checkbox
                                        v-model="defaultPaySection.brute"
                                        :label="`${$t('pay_section.in_brute')}`"
                                        true-value="1"
                                        false-value="0"
                                        hide-details
                                        color="cyan"
                                        :disabled="!defaultPaySection.conditioned"
                                        :readonly="readonly"
                                    ></v-checkbox>
                                </v-col>
                                <v-col cols="6">
                                    <v-checkbox
                                        v-model="defaultPaySection.regularization"
                                        :label="`${$t('pay_section.in_regul')}`"
                                        true-value="1"
                                        false-value="0"
                                        hide-details
                                        color="purple"
                                        :disabled="!defaultPaySection.conditioned"
                                        :readonly="readonly"
                                    ></v-checkbox>
                                </v-col>
                                <v-col cols="6">
                                    <v-checkbox
                                        v-model="defaultPaySection.conge"
                                        :label="`${$t('pay_section.in_holiday')}`"
                                        true-value="1"
                                        false-value="0"
                                        hide-details
                                        color="teal"
                                        :disabled="!defaultPaySection.conditioned"
                                        :readonly="readonly"
                                    ></v-checkbox>
                                </v-col>
                                <v-col cols="6">
                                    <v-checkbox
                                        v-model="defaultPaySection.isAbsenceRetenue"
                                        color="red"
                                        :label="$t('pay_section.is_absence_retenue')"
                                        inset
                                        hide-details
                                        :disabled="!defaultPaySection.conditioned"
                                        :readonly="readonly"
                                    ></v-checkbox>
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>